import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { useAdminGuard } from "../../hooks/usePermissionGuard";
import { Users, BookOpen, DollarSign, TrendingUp, TrendingDown, Search, Eye, Award, Calendar, Star, ArrowLeft, Home } from "lucide-react";

interface InstructorStats {
  uid: string;
  name: string;
  email: string;
  profileImage?: string;
  totalCourses: number;
  totalStudents: number;
  totalRevenue: number;
  subscriptionTier: string;
  joinDate: Date | null;
}

export default function SystemDashboard() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instructors, setInstructors] = useState<InstructorStats[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<'revenue' | 'students' | 'courses' | 'name'>('revenue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [totalStats, setTotalStats] = useState({
    totalInstructors: 0,
    totalStudents: 0,
    totalCourses: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    // Check if user is admin
    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/not-allowed");
      return;
    }
    loadSystemData();
  }, [user, navigate]);

  async function loadSystemData() {
    try {
      // Load all users
      const usersSnap = await getDocs(collection(db, "users"));
      const allUsers = usersSnap.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
      
      console.log('[SystemDashboard] Total users:', allUsers.length);
      console.log('[SystemDashboard] All users:', allUsers.map(u => ({ email: u.email, role: u.role })));

      // Filter instructors (include admins)
      const instructorUsers = allUsers.filter((u: any) => u.role === "instructor" || u.role === "admin");
      const studentUsers = allUsers.filter((u: any) => u.role === "student");
      
      console.log('[SystemDashboard] Instructors found:', instructorUsers.length);
      console.log('[SystemDashboard] Instructor emails:', instructorUsers.map(u => u.email));

      // Load all courses
      const coursesSnap = await getDocs(collection(db, "courses"));
      const allCourses = coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Load all enrollments (from progress collection)
      const progressSnap = await getDocs(collection(db, "progress"));
      const allProgress = progressSnap.docs.map(doc => doc.data());

      // Load all payments (course payments for instructor revenue) - only instructor-approved
      const paymentsQuery = query(
        collection(db, "payments"),
        where("status", "==", "completed")
      );
      const paymentsSnap = await getDocs(paymentsQuery);
      const allPayments = paymentsSnap.docs
        .filter(doc => doc.data().approvedBy)
        .map(doc => doc.data());

      // Load subscription payments (revenue from instructors to us) - only admin-approved
      const subscriptionPaymentsSnap = await getDocs(collection(db, "subscription_payments"));
      const subscriptionPayments = subscriptionPaymentsSnap.docs
        .filter(doc => doc.data().status === 'approved')
        .map(doc => doc.data());
      const totalSubscriptionRevenue = subscriptionPayments.reduce(
        (sum: number, payment: any) => sum + (Number(payment.amount) || 0), 
        0
      );

      // Calculate stats for each instructor
      const instructorStatsList: InstructorStats[] = [];

      for (const instructor of instructorUsers) {
        // Count courses
        const instructorCourses = allCourses.filter((c: any) => c.instructorId === instructor.uid);
        const courseIds = instructorCourses.map((c: any) => c.id);

        // Count unique students
        const studentIds = new Set(
          allProgress
            .filter((p: any) => courseIds.includes(p.courseId))
            .map((p: any) => p.userId)
        );

        // Calculate revenue from subscription payments (what instructor paid us)
        const totalRevenue = subscriptionPayments
          .filter((payment: any) => payment.instructorId === instructor.uid)
          .reduce((sum: number, payment: any) => sum + Number(payment.amount || 0), 0);

        // Handle createdAt conversion safely
        let joinDate = null;
        try {
          if (instructor.createdAt) {
            if (typeof instructor.createdAt.toDate === 'function') {
              joinDate = instructor.createdAt.toDate();
            } else if (instructor.createdAt instanceof Date) {
              joinDate = instructor.createdAt;
            }
          }
        } catch (error) {
          console.error('[SystemDashboard] Error converting createdAt for', instructor.email, error);
        }

        instructorStatsList.push({
          uid: instructor.uid,
          name: instructor.name || "ไม่ระบุชื่อ",
          email: instructor.email || "",
          profileImage: instructor.profileImage,
          totalCourses: instructorCourses.length,
          totalStudents: studentIds.size,
          totalRevenue,
          subscriptionTier: instructor.plan || "free",
          joinDate
        });
      }

      // Sort by revenue (highest first)
      instructorStatsList.sort((a, b) => b.totalRevenue - a.totalRevenue);

      console.log('[SystemDashboard] Instructor stats list:', instructorStatsList);
      console.log('[SystemDashboard] Setting instructors state with', instructorStatsList.length, 'items');

      setInstructors(instructorStatsList);
      setTotalStats({
        totalInstructors: instructorUsers.length,
        totalStudents: studentUsers.length,
        totalCourses: allCourses.length,
        totalRevenue: totalSubscriptionRevenue // Revenue from instructors paying us
      });

      console.log('[SystemDashboard] State updated, loading set to false');
      setLoading(false);
    } catch (error) {
      console.error("Error loading system data:", error);
      setLoading(false);
    }
  }

  const filteredInstructors = instructors
    .filter(instructor => {
      const matchesSearch = instructor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        instructor.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesTier = filterTier === 'all' || instructor.subscriptionTier.toLowerCase() === filterTier.toLowerCase();
      
      return matchesSearch && matchesTier;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'revenue':
          comparison = a.totalRevenue - b.totalRevenue;
          break;
        case 'students':
          comparison = a.totalStudents - b.totalStudents;
          break;
        case 'courses':
          comparison = a.totalCourses - b.totalCourses;
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name, 'th');
          break;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "ไม่มีข้อมูล";
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูลระบบ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-800" />
              </button>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-pink-600 rounded-xl flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
                <p className="text-gray-600">ภาพรวมระบบทั้งหมด</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>
        </div>

        {/* System Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">จำนวนครู</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalInstructors}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">จำนวนผู้เรียน</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalStudents}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">คอร์สทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{totalStats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-yellow-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">รายได้รวม</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalStats.totalRevenue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อหรืออีเมลครู..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-gray-800 shadow-sm"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">เรียงตาม:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm font-medium"
              >
                <option value="revenue">รายได้</option>
                <option value="students">จำนวนนักเรียน</option>
                <option value="courses">จำนวนคอร์ส</option>
                <option value="name">ชื่อ</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">ลำดับ:</span>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg hover:border-blue-500 transition-colors text-sm font-medium flex items-center gap-2"
              >
                {sortOrder === 'desc' ? (
                  <>
                    <TrendingDown className="w-4 h-4" />
                    มาก → น้อย
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4" />
                    น้อย → มาก
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">แพ็กเกจ:</span>
              <select
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm font-medium"
              >
                <option value="all">ทั้งหมด</option>
                <option value="free">FREE</option>
                <option value="pro">PRO</option>
                <option value="pro_plus">PRO_PLUS</option>
                <option value="pro_yearly">PRO_YEARLY</option>
              </select>
            </div>

            {(searchTerm || filterTier !== 'all' || sortBy !== 'revenue' || sortOrder !== 'desc') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterTier('all');
                  setSortBy('revenue');
                  setSortOrder('desc');
                }}
                className="ml-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
              >
                รีเซ็ตตัวกรอง
              </button>
            )}
          </div>
        </div>

        {/* Instructors List */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">รายงานครูทั้งหมด</h2>
            <span className="text-gray-600">{filteredInstructors.length} คน</span>
          </div>

          {filteredInstructors.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">ไม่พบข้อมูล</h3>
              <p className="text-gray-600">
                {searchTerm ? "ไม่พบครูที่ค้นหา" : "ยังไม่มีครูในระบบ"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">ครู</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">คอร์ส</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">นักเรียน</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">รายได้</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">แพ็คเกจ</th>
                    <th className="text-left py-4 px-4 font-semibold text-gray-700">วันที่สมัคร</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-700">การกระทำ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstructors.map((instructor) => (
                    <tr key={instructor.uid} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          {instructor.profileImage ? (
                            <img
                              src={instructor.profileImage}
                              alt={instructor.name}
                              className="w-12 h-12 rounded-full object-cover ring-2 ring-purple-100"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-pink-600 flex items-center justify-center text-white text-lg font-bold">
                              {instructor.name[0]?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-800">{instructor.name}</p>
                            <p className="text-sm text-gray-600">{instructor.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-gray-800">{instructor.totalCourses}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-gray-800">{instructor.totalStudents}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-700">{formatCurrency(instructor.totalRevenue)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          instructor.subscriptionTier === "yearly"
                            ? "bg-yellow-100 text-yellow-700"
                            : instructor.subscriptionTier === "pro_plus"
                            ? "bg-pink-100 text-pink-700"
                            : instructor.subscriptionTier === "pro"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-700"
                        }`}>
                          {instructor.subscriptionTier.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">{formatDate(instructor.joinDate)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Link to={`/admin/instructor/${instructor.uid}`}>
                          <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-4 py-2 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto">
                            <Eye className="w-4 h-4" />
                            ดูรายละเอียด
                          </button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
